import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    devicesList,
    getDevicesForAudio,
    attachDeviceToAudio,
    detachDeviceFromAudio,
    reorderDevicesForAudio,
} from '../api/audio';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import './AudioPlaylists.scss';

export default function AudioByDevicesDetail() {
    const { id } = useParams();           // id = audioId
    const audioId = Number(id || 0);

    const [loading, setLoading] = useState(false);
    const [audio, setAudio] = useState(null);     // {id,hash,filename}
    const [attached, setAttached] = useState([]); // [{device:{id,code}, position}]
    const [allDevices, setAllDevices] = useState([]);
    const [addingId, setAddingId] = useState(null);
    const [savingOrder, setSavingOrder] = useState(false);

    const orderedDeviceIds = useMemo(
        () => attached.map(x => x.device.id),
        [attached]
    );

    useEffect(() => { if (audioId) load(); }, [audioId]);

    async function load() {
        setLoading(true);
        try {
            const [devs, info] = await Promise.all([
                devicesList(),
                getDevicesForAudio(audioId),
            ]);
            setAllDevices(devs);
            setAudio(info.audio);
            setAttached(info.items || []);
        } catch (e) {
            alert(e.message || 'Ошибка загрузки');
        } finally {
            setLoading(false);
        }
    }

    async function add(deviceId) {
        setAddingId(deviceId);
        try {
            await attachDeviceToAudio(audioId, deviceId); // в конец
            await load();
        } catch (e) {
            alert(e.message);
        } finally {
            setAddingId(null);
        }
    }

    async function remove(deviceId) {
        try {
            await detachDeviceFromAudio(audioId, deviceId);
            await load();
        } catch (e) {
            alert(e.message);
        }
    }

    function onDragEnd(result) {
        if (!result.destination) return;
        const items = Array.from(attached);
        const [rem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, rem);
        setAttached(items);
    }

    async function saveOrder() {
        setSavingOrder(true);
        try {
            const orders = attached.map((it, idx) => ({ deviceId: it.device.id, position: idx }));
            await reorderDevicesForAudio(audioId, orders);
        } catch (e) {
            alert(e.message);
        } finally {
            setSavingOrder(false);
        }
    }

    const attachedSet = new Set(orderedDeviceIds);
    const available = allDevices.filter(d => !attachedSet.has(d.id));

    return (
        <div className="a-pl-detail">
            {loading ? <div className="loading">Загрузка...</div> : (
                <>
                    <div className="head">
                        <h2>Аудио #{audio?.id} — {audio?.filename || audio?.hash}</h2>
                        <div className="head-actions">
                            <Link to="/audio-devices" className="btn">Назад</Link>
                            <button className="primary" disabled={savingOrder} onClick={saveOrder}>
                                {savingOrder ? 'Сохраняю...' : 'Сохранить порядок'}
                            </button>
                        </div>
                    </div>

                    <div className="columns">
                        {/* Устройства, где играет это аудио */}
                        <div className="col">
                            <h3>Воспроизводится на устройствах</h3>
                            <DragDropContext onDragEnd={onDragEnd}>
                                <Droppable droppableId="devlist">
                                    {(provided) => (
                                        <div className="list playlist" ref={provided.innerRef} {...provided.droppableProps}>
                                            {attached.length === 0 && <div className="empty">Пусто</div>}
                                            {attached.map((it, idx) => (
                                                <Draggable draggableId={`dev-${it.device.id}`} index={idx} key={`dev-${it.device.id}`}>
                                                    {(pp, snap) => (
                                                        <div
                                                            className={`row ${snap.isDragging ? 'dragging':''}`}
                                                            ref={pp.innerRef}
                                                            {...pp.draggableProps}
                                                            {...pp.dragHandleProps}
                                                        >
                                                            <div className="row-main">
                                                                <div className="title">Устройство #{it.device.id}</div>
                                                                <div className="meta">код: {it.device.code} • позиция: {idx}</div>
                                                            </div>
                                                            <div className="row-actions">
                                                                <button className="danger" onClick={() => remove(it.device.id)}>Убрать</button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        </div>

                        {/* Доступные устройства для добавления */}
                        <div className="col">
                            <h3>Добавить на устройство</h3>
                            <div className="list devices">
                                {available.length === 0 ? <div className="empty">Нет доступных устройств</div> :
                                    available.map(d => (
                                        <div key={d.id} className="row">
                                            <div className="row-main">
                                                <div className="title">Устройство #{d.id}</div>
                                                <div className="meta">код: {d.code}</div>
                                            </div>
                                            <div className="row-actions">
                                                <button
                                                    className="primary"
                                                    disabled={addingId===d.id}
                                                    onClick={() => add(d.id)}
                                                >
                                                    {addingId===d.id ? 'Добавляю...' : 'Добавить'}
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                }
                            </div>
                        </div>

                        {/* Справка по аудио */}
                        <div className="col">
                            <h3>Информация</h3>
                            <div className="list">
                                <div className="row">
                                    <div className="row-main">
                                        <div className="title">Hash</div>
                                        <div className="meta">{audio?.hash}</div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="row-main">
                                        <div className="title">Имя</div>
                                        <div className="meta">{audio?.filename || '—'}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </>
            )}
        </div>
    );
}
